// EXPERIMENTAL — orchestrion code-transform loader + webpack plugin. The loader is exposed
// separately because Turbopack can only take webpack loaders (via `turbopack.rules`), not plugins.

import { createRequire } from 'node:module';
import { dirname } from 'node:path';
import type { Compiler } from 'webpack';
import type { InstrumentationConfig } from '..';
import { instrumentedModuleNames, SENTRY_INSTRUMENTATIONS } from '../config';
import codeTransformerWebpack from '@apm-js-collab/code-transformer-bundler-plugins/webpack';
import type { PluginOptions } from './options';

export { serializeInstrumentations } from '@apm-js-collab/code-transformer-bundler-plugins/core';
export type { SerializableInstrumentationConfig } from '@apm-js-collab/code-transformer-bundler-plugins/core';
import { externalEntryMatchesModule, externalizedModulesWarning, orchestrionTransformOptions } from './options';

// Both branches use `createRequire` (never alias the CJS `require`) so bundlers consuming this
// module don't emit a "Critical dependency" warning.
function getOrchestrionRequire(): ReturnType<typeof createRequire> {
  let nodeRequire: ReturnType<typeof createRequire>;
  /*! rollup-include-cjs-only */
  nodeRequire = createRequire(__filename);
  /*! rollup-include-cjs-only-end */
  /*! rollup-include-esm-only */
  nodeRequire = createRequire(import.meta.url);
  /*! rollup-include-esm-only-end */
  return nodeRequire;
}

/** Absolute path to the code-transform loader (a webpack loader; also usable as a Turbopack loader). */
export function getOrchestrionLoaderPath(): string {
  return getOrchestrionRequire().resolve('@apm-js-collab/code-transformer-bundler-plugins/webpack-loader');
}

/**
 * Absolute path to the `@apm-js-collab/tracing-hooks` package directory, resolved from this
 * package's own dependency graph. SDKs inject it at build time so the runtime module hook can
 * load the package even where the bare specifier doesn't resolve (bundled SDK code under
 * isolated installs, e.g. pnpm).
 */
export function getTracingHooksDirectory(): string {
  const packageJsonPath = getOrchestrionRequire().resolve('@apm-js-collab/tracing-hooks/package.json');
  // This avoids any backslash-escaping concerns on Windows
  return dirname(packageJsonPath).replace(/\\/g, '/');
}

/** The central instrumentation config, to pass as the loader's `instrumentations` option. */
export function getSentryInstrumentations(): InstrumentationConfig[] {
  return SENTRY_INSTRUMENTATIONS;
}

// Handles the declarative `externals` shapes (string, RegExp, object, arrays
// thereof). Function externals (e.g. webpack-node-externals) are skipped: they
// may resolve asynchronously, so they can't be probed reliably here.
function externalizedWebpackModules(externals: unknown, moduleNames: string[]): string[] {
  const entries = Array.isArray(externals) ? externals : [externals];
  return moduleNames.filter(name =>
    entries.some(entry => {
      if (typeof entry === 'string') {
        return externalEntryMatchesModule(entry, name);
      }
      if (entry instanceof RegExp) {
        return entry.test(name);
      }
      if (entry && typeof entry === 'object') {
        return name in entry;
      }
      return false;
    }),
  );
}

/**
 * The code-transform webpack plugin, pre-fed the instrumentation config.
 *
 * Instrumented packages marked as `externals` never pass through the code
 * transform, so a compilation warning is emitted for them.
 */
export function sentryOrchestrionWebpackPlugin(options: PluginOptions = {}): ReturnType<typeof codeTransformerWebpack> {
  const plugin = codeTransformerWebpack(orchestrionTransformOptions(options));
  const moduleNames = instrumentedModuleNames(options.instrumentations);
  // The upstream plugin is a class instance, so `apply` is overridden in place
  // rather than spread into a new object (which would lose prototype methods).
  const apply = plugin.apply.bind(plugin);
  plugin.apply = (compiler: Compiler): void => {
    const externalizedModules = externalizedWebpackModules(compiler.options.externals, moduleNames);
    if (externalizedModules.length > 0) {
      compiler.hooks.thisCompilation.tap('SentryOrchestrionExternalsCheck', compilation => {
        compilation.warnings.push(new compiler.webpack.WebpackError(externalizedModulesWarning(externalizedModules)));
      });
    }
    apply(compiler);
  };
  return plugin;
}
